import type {
  EditVehicleServicePlanPromptResolve,
  VehicleServicePlanNode,
} from '@/modules/vehicles'
import { getVehicleServicePlanInput } from '@/modules/vehicles/util'
import { EditVehicleServicePlanModal } from '@/modules/vehicles/fleet/components/edit-vehicle-service-plan-modal'
import {
  useCreateWorkshopByName,
  useVehicleServicePlanConversionMutations,
  useVehicleServicePlanMutations,
} from '@/modules/vehicles/fleet/mutations.hooks'
import { format } from '@/utils/date-fns'
import { useConfirm } from '@/utils/hooks'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'

/** Add/edit/delete/move handlers for the fleet-wide vehicle services page. */
export function useVehicleServicePlanHandlers() {
  const translations = useTranslate({
    delete: 'vehicles.prompts.delete-vehicle-service',
    deleteConfirmation: [
      'documents.prompts.delete-document-confirmation',
      { name: '' },
    ],
    moveToExpirations: 'vehicles.services.move-to-expirations',
    moveConfirmation: [
      'vehicles.prompts.move-to-expirations-confirmation',
      { name: '' },
    ],
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()

  const { createServicePlan, patchServicePlan, deleteServicePlan } =
    useVehicleServicePlanMutations()
  const { convertServicePlanToExpiration } =
    useVehicleServicePlanConversionMutations()
  const createWorkshopByName = useCreateWorkshopByName()

  async function addServicePlan() {
    const { data } =
      await addPrompt<EditVehicleServicePlanPromptResolve | null>(resolve => (
        <EditVehicleServicePlanModal
          askForVehicle
          onCreateWorkshop={createWorkshopByName}
          onSubmit={resolve}
        />
      ))
    if (!data) return

    await createServicePlan({
      variables: {
        input: {
          ...getVehicleServicePlanInput(data.form, data.hasNotification),
          // Asked for in the modal here: unlike the per-vehicle tab, this page
          // has no vehicle of its own to attach the service to.
          vehicle: data.form.vehicle ?? undefined,
        },
      },
    })
  }

  async function editServicePlan(servicePlan: VehicleServicePlanNode) {
    const { data } =
      await addPrompt<EditVehicleServicePlanPromptResolve | null>(resolve => (
        <EditVehicleServicePlanModal
          servicePlan={servicePlan}
          onCreateWorkshop={createWorkshopByName}
          onSubmit={resolve}
        />
      ))
    if (!data) return

    await patchServicePlan({
      variables: {
        id: servicePlan.id,
        input: getVehicleServicePlanInput(data.form, data.hasNotification),
      },
    })
  }

  async function removeServicePlan(servicePlan: VehicleServicePlanNode) {
    const { data: answer } = await confirm(
      translations.deleteConfirmation({ name: describe(servicePlan) }),
      translations.delete
    )
    if (!answer) return

    await deleteServicePlan({ variables: { id: servicePlan.id } })
  }

  async function moveToExpirations(servicePlan: VehicleServicePlanNode) {
    const { data: answer } = await confirm(
      translations.moveConfirmation({ name: describe(servicePlan) }),
      translations.moveToExpirations
    )
    if (!answer) return

    await convertServicePlanToExpiration({ variables: { id: servicePlan.id } })
  }

  return {
    addServicePlan,
    editServicePlan,
    removeServicePlan,
    moveToExpirations,
  }
}

function describe(servicePlan: VehicleServicePlanNode) {
  return `${format(new Date(servicePlan.scheduledStart), 'PP')} - ${
    servicePlan.name
  }`
}
